import { QuestionIcon, WarningIcon } from '@phosphor-icons/react'
import { typography } from '@pixee/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import { createFormHook, createFormHookContexts, useStore } from '@tanstack/react-form'
import classNames from 'classnames'
import React, { useCallback, useState } from 'react'
import { match } from 'ts-pattern'
import { CreateWorkflowRequest, Repository, Scale, ToolSchema, type Tool } from '../../utils/api-client/api-schemas.ts'
import { getToolName } from '../../utils/repository-utils.tsx'
import { getSeveritiesByRankDescending } from '../../utils/scale-utils.ts'
import { capitalizeFirstLetter } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { Checkbox } from '../input/checkbox'
import { InputContainer } from '../input/input-container'
import { InputGroup } from '../input/input-group'
import { SelectBox } from '../input/select-box.tsx'
import * as inputStyles from '../input/shared-input.css.ts'
import { Slider } from '../input/slider'
import { TextInput } from '../input/text-input'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import { TooltipWithHover } from '../tooltip'
import * as styles from './workflow-form.css'
import * as sharedStyles from './workflow-shared.css'

export type CommonWorkflowParams = {
  name?: string
  tool: Tool
  selectedSeverities?: string[]
  minimumSeverityScore?: number
  maximumSeverityScore?: number
  fixConfidence?: 'high' | 'medium' | 'low'
}

export type WorkflowParams = CommonWorkflowParams & {
  selectedAction: WorkflowActionType
} & (
    | {
        type: 'scan' | 'pull-request-scan'
        branch: string
        limitFixes: boolean
        fixesLimit?: number
      }
    | {
        type: 'scheduled'
        branch: string
        cadence: 'daily' | 'weekly'
      }
  )

export type WorkflowType = WorkflowParams['type']

export type WorkflowActionType = CreateWorkflowRequest['action']['type']

export type CreateWorkflowFormValues = {
  selectedType: WorkflowType | undefined
  selectedTool: Tool | undefined
  selectedCadence: string | undefined
  workflowName: string
  branch: string
  limitFixes: boolean
  fixesLimit: number
  selectedSeverities: string[]
  selectedRange: [number, number]
  fixConfidence: 'high' | 'medium' | 'low' | undefined
  selectedAction: WorkflowActionType
}

const FORM_DEFAULT_VALUES: CreateWorkflowFormValues = {
  selectedType: undefined,
  selectedTool: undefined,
  selectedCadence: undefined,
  workflowName: '',
  branch: '',
  limitFixes: true,
  fixesLimit: 1,
  selectedSeverities: [],
  selectedRange: [0, 10],
  fixConfidence: undefined,
  selectedAction: 'create-patch',
}

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

export const useCreateWorkflowForm = (defaultValueOverrides: Partial<CreateWorkflowFormValues> = {}) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

const TOOLS_SHOWING_SLIDER = ['polaris_sast', 'codeql', 'checkmarx']

type CreateWorkflowFormProps = {
  scaleItems: Scale[]
}

export const CreateWorkflowForm: React.FC<CreateWorkflowFormProps> = ({ scaleItems }) => {
  const form = useFormContext()

  const selectedType = useStore(form.store, state => state.values.selectedType)
  const selectedTool = useStore(form.store, state => state.values.selectedTool)
  const selectedCadence = useStore(form.store, state => state.values.selectedCadence)
  const workflowName = useStore(form.store, state => state.values.workflowName)
  const branch = useStore(form.store, state => state.values.branch)
  const selectedRange = useStore(form.store, state => state.values.selectedRange)
  const selectedAction = useStore(form.store, state => state.values.selectedAction)

  const cvssScale = scaleItems.find(scale => scale.id === 'cvss')
  const cvssMin = cvssScale?.type === 'score' ? (cvssScale.min ?? 0) : 0
  const cvssMax = cvssScale?.type === 'score' ? (cvssScale.max ?? 10) : 10

  const selectedScale = getScaleForTool(scaleItems, selectedTool)
  const severities = getSeveritiesByRankDescending(selectedScale)

  const renderSeveritySection = () => {
    if (!selectedTool) {
      return null
    }

    const toolsUsingSarif = ['semgrep', 'snyk']

    if (TOOLS_SHOWING_SLIDER.includes(selectedTool)) {
      return (
        <div className={styles.severityContainer}>
          <InputContainer label="Severity range" explicitLabelCondition="optional">
            <Slider
              min={cvssMin}
              max={cvssMax}
              value={selectedRange}
              onValueChange={value => form.setFieldValue('selectedRange', value)}
              aria-label="Severity threshold range"
            />
          </InputContainer>
        </div>
      )
    }

    const sarifScale = scaleItems.find(scale => scale.id === 'sarif-levels')
    const labelledSeverities = toolsUsingSarif.includes(selectedTool)
      ? getSeveritiesByRankDescending(sarifScale)
      : severities

    return <SeveritySelector severities={labelledSeverities} />
  }

  const handleToolChange = useCallback(
    (value: string) => {
      form.setFieldValue('selectedTool', ToolSchema.parse(value))
    },
    [form]
  )

  const handleTypeChange = useCallback(
    (value: string) => {
      const type = value as WorkflowType
      form.setFieldValue('selectedType', type)
      // Analyzing pull requests should not cap the number of fixes per PR by default.
      form.setFieldValue('limitFixes', type !== 'pull-request-scan')
    },
    [form]
  )

  return (
    <form className={styles.workflowFormContainer}>
      <InputGroup title="What would you like to automate?">
        <SelectBox
          label="Workflow"
          name="workflowType"
          placeholder="Select workflow"
          value={selectedType}
          onValueChange={handleTypeChange}
          options={
            [
              { value: 'scheduled', label: 'Schedule fixes' },
              { value: 'scan', label: 'Analyze new scans' },
              { value: 'pull-request-scan', label: 'Analyze pull requests' },
            ] satisfies { value: WorkflowType; label: string }[]
          }
        />
      </InputGroup>

      {selectedType === 'scheduled' && (
        <>
          <div className={styles.findingsContainer}>
            <div className={styles.sectionHeading}>Which findings are you monitoring?</div>
            <SelectBox
              label="Tool"
              name="tool"
              value={selectedTool}
              onValueChange={handleToolChange}
              options={ToolSchema.options.map(tool => {
                const name = getToolName(tool)
                return {
                  value: tool,
                  label: (
                    <div className={styles.toolOptionLabel}>
                      <ToolIcon tool={tool} size="medium" />
                      {name}
                    </div>
                  ),
                }
              })}
              placeholder="Select tool"
            />
          </div>

          <InputGroup title="Which branch(es) are you targeting?">
            <BranchPrefixInput branch={branch} setBranch={value => form.setFieldValue('branch', value)} />
          </InputGroup>

          <FixesCustomizationSelector severitySection={selectedTool ? renderSeveritySection() : null} />

          <div className={styles.findingsContainer}>
            <div className={styles.sectionHeading}>Define a schedule</div>
            <SelectBox
              label="Cadence"
              name="cadence"
              value={selectedCadence}
              onValueChange={value => form.setFieldValue('selectedCadence', value)}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
              ]}
              placeholder="Select cadence"
            />
          </div>

          <div className={styles.flexContainer}>
            <TextInput
              id="workflowName"
              name="workflowName"
              label="Name this workflow"
              value={workflowName}
              onChange={value => form.setFieldValue('workflowName', value)}
              placeholder="Override the default workflow name with your own"
              explicitLabelCondition="optional"
            />
          </div>
        </>
      )}

      {(selectedType === 'scan' || selectedType === 'pull-request-scan') && (
        <div className={styles.scanSectionContainer}>
          <div className={styles.sectionGroup}>
            <div className={styles.sectionHeading}>Which findings are you monitoring?</div>
            <SelectBox
              label="Tool"
              name="tool"
              value={selectedTool}
              onValueChange={handleToolChange}
              options={ToolSchema.options.map(tool => {
                const name = getToolName(tool)
                return {
                  value: tool,
                  label: (
                    <div className={styles.toolOptionLabel}>
                      <ToolIcon tool={tool} size="medium" />
                      {name}
                    </div>
                  ),
                }
              })}
              placeholder="Select tool"
            />
          </div>

          <InputGroup
            title={
              selectedType === 'pull-request-scan'
                ? 'Which branch are you comparing against?'
                : 'Which branch(es) are you targeting?'
            }
          >
            <BranchPrefixInput
              branch={branch}
              setBranch={value => form.setFieldValue('branch', value)}
              helperText={
                selectedType === 'pull-request-scan'
                  ? 'The base branch pull requests are opened against. Use * for prefix matching. Default branch used if empty.'
                  : undefined
              }
            />
          </InputGroup>

          <div className={styles.sectionGroup}>
            <div className={styles.sectionHeading}>Configure the action to take</div>
            <SelectBox
              label="Action"
              value={selectedAction}
              onValueChange={value => form.setFieldValue('selectedAction', value as WorkflowActionType)}
              options={
                [
                  { value: 'none', label: 'Analyze only' },
                  { value: 'create-patch', label: 'Analyze and fix' },
                ] satisfies { value: WorkflowActionType; label: string }[]
              }
            />
            {selectedAction === 'create-patch' && (
              <FixesCustomizationSelector severitySection={selectedTool ? renderSeveritySection() : null} />
            )}
          </div>

          <div className={styles.flexContainer}>
            <TextInput
              id="workflowName"
              name="workflowName"
              label="Name this workflow"
              value={workflowName}
              onChange={value => form.setFieldValue('workflowName', value)}
              placeholder="Override our default workflow name with your own"
              explicitLabelCondition="optional"
            />
          </div>
        </div>
      )}
    </form>
  )
}

type BranchPrefixInputProps = {
  branch: string
  setBranch: (value: string) => unknown
  helperText?: string
}

const BranchPrefixInput = ({ branch, setBranch, helperText }: BranchPrefixInputProps) => {
  const [isTouched, setIsTouched] = useState(false)
  const hasTrailingSlashError = branch.endsWith('/')
  const showError = isTouched && hasTrailingSlashError

  return (
    <div className={styles.flexContainer}>
      <TextInput
        id="branch"
        name="branch"
        label="Branch / Prefix"
        value={branch}
        onChange={setBranch}
        onBlur={() => setIsTouched(true)}
        placeholder="Enter a branch name or prefix pattern (requires *)"
        explicitLabelCondition="optional"
        errorText={showError ? "A trailing '*' is required for prefix pattern matching" : undefined}
        isError={showError}
      />
      {!showError && (
        <div className={styles.branchHelperRow}>
          <span>
            {helperText ??
              'Exact match by default. Use * to match multiple branches. Default branch will be used if nothing is specified.'}
          </span>
          <TooltipWithHover
            side="top"
            trigger={
              <span className={styles.branchTooltipIcon} aria-label="Branch pattern examples">
                <QuestionIcon size={14} weight="light" />
              </span>
            }
          >
            <div className={styles.branchTooltipContent}>
              <div>
                <strong>main</strong> <em>(exact match; indicates a single branch)</em>
              </div>
              <div>
                <strong>feature/*</strong> <em>(prefix; indicates multiple branches)</em>
              </div>
            </div>
          </TooltipWithHover>
        </div>
      )}
    </div>
  )
}

export const getDefaultWorkflowName = (tool: Tool | undefined, cadence: string | undefined) => {
  if (!tool || !cadence) {
    return ''
  }
  const toolName = getToolName(tool)
  return `${cadence === 'daily' ? 'Daily' : 'Weekly'} ${toolName} fixes`
}

const getScaleForTool = (scaleItems: Scale[], tool: Tool | undefined) => {
  if (!tool) {
    return undefined
  }

  return match(tool)
    .with('appscan', () => scaleItems.find(scale => scale.id === 'appscan'))
    .with('veracode', () => scaleItems.find(scale => scale.id === 'veracode'))
    .otherwise(() => scaleItems.find(scale => scale.id === 'sonar-merged'))
}

type Severity = {
  label: string
}

type SeveritySelectorProps = {
  severities?: Severity[]
}

const SeveritySelector = ({ severities }: SeveritySelectorProps) => {
  const form = useFormContext()
  const selectedSeverities = useStore(form.store, state => state.values.selectedSeverities)

  if (!severities?.length) {
    return null
  }

  return (
    <div className={styles.severityContainer}>
      <div className={inputStyles.labelContainer}>
        <label className={inputStyles.inputLabel}>Severity</label>
        <span className={inputStyles.optionalText}>(Optional)</span>
      </div>
      <div className={styles.checkboxGroup}>
        {severities.map(severity => (
          <div key={severity.label} className={styles.severityItem}>
            <Checkbox
              id={severity.label.toLowerCase()}
              checked={selectedSeverities.includes(severity.label.toLowerCase())}
              onCheckedChange={checked => {
                const label = severity.label.toLowerCase()
                const next = checked ? [...selectedSeverities, label] : selectedSeverities.filter(s => s !== label)
                form.setFieldValue('selectedSeverities', next)
              }}
              state="default"
            />
            <label htmlFor={severity.label.toLowerCase()} className={`${typography.paragraph} ${styles.severityLabel}`}>
              {capitalizeFirstLetter(severity.label)}
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}

type FixConfidence = 'high' | 'medium' | 'low'

const isFixConfidence = (value: string): value is FixConfidence => {
  return ['high', 'medium', 'low'].includes(value)
}

type FixesCustomizationSelectorProps = {
  severitySection?: React.ReactNode
}

const FixesCustomizationSelector: React.FC<FixesCustomizationSelectorProps> = ({ severitySection }) => {
  const form = useFormContext()
  const fixConfidence = useStore(form.store, state => state.values.fixConfidence)
  const limitFixes = useStore(form.store, state => state.values.limitFixes)
  const fixesLimit = useStore(form.store, state => state.values.fixesLimit)

  const onValueChange = useCallback(
    (value: string) => {
      if (value === undefined || isFixConfidence(value)) {
        form.setFieldValue('fixConfidence', value)
      } else {
        console.warn(`Invalid fix confidence value: ${value}`)
      }
    },
    [form]
  )

  return (
    <InputGroup title="Customize the fixes you receive">
      <div className={styles.sectionGroup}>
        {severitySection}
        <SelectBox
          label="Minimum fix confidence"
          explicitLabelCondition="optional"
          placeholder="Select confidence level"
          value={fixConfidence}
          onValueChange={onValueChange}
          options={
            [
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
            ] satisfies { value: FixConfidence; label: string }[]
          }
        />

        <div className={styles.limitFixesRow}>
          <div className={styles.limitFixesCheckbox}>
            <Checkbox
              id="limit-fixes"
              checked={limitFixes}
              onCheckedChange={(checked: boolean | 'indeterminate') =>
                form.setFieldValue('limitFixes', checked === true)
              }
              state="default"
            />
            <label htmlFor="limit-fixes" className={styles.limitFixesLabel}>
              Limit the number of fixes per pull request
            </label>
          </div>
          <TextInput
            className={classNames(...(limitFixes ? [] : [styles.hidden]))}
            type="number"
            value={fixesLimit.toString()}
            onChange={value => form.setFieldValue('fixesLimit', Math.max(1, parseInt(value) || 1))}
            width={80}
          />
        </div>
      </div>
    </InputGroup>
  )
}

const getWorkflowParams = (values: CreateWorkflowFormValues, scaleItems: Scale[]): WorkflowParams | undefined => {
  const {
    selectedType,
    selectedTool,
    selectedCadence,
    workflowName,
    branch,
    limitFixes,
    fixesLimit,
    selectedSeverities,
    selectedRange,
    fixConfidence,
    selectedAction,
  } = values

  if (!selectedTool || !selectedType || (selectedType === 'scheduled' && !selectedCadence)) {
    return undefined
  }

  const cvssScale = scaleItems.find(scale => scale.id === 'cvss')
  const cvssMin = cvssScale?.type === 'score' ? (cvssScale.min ?? 0) : 0
  const cvssMax = cvssScale?.type === 'score' ? (cvssScale.max ?? 10) : 10

  const commonParams: CommonWorkflowParams = {
    name: workflowName || undefined,
    tool: selectedTool,
    selectedSeverities,
    fixConfidence,
  }

  if (TOOLS_SHOWING_SLIDER.includes(selectedTool)) {
    if (selectedRange[0] !== cvssMin) {
      commonParams.minimumSeverityScore = selectedRange[0]
    }
    if (selectedRange[1] !== cvssMax) {
      commonParams.maximumSeverityScore = selectedRange[1]
    }
  }

  return match(selectedType)
    .with(
      'scan',
      'pull-request-scan',
      type =>
        ({
          ...commonParams,
          type,
          branch,
          limitFixes,
          fixesLimit: limitFixes ? fixesLimit : undefined,
          selectedAction,
        }) satisfies WorkflowParams
    )
    .with(
      'scheduled',
      type =>
        ({
          ...commonParams,
          type,
          branch,
          cadence: selectedCadence as 'daily' | 'weekly',
          selectedAction,
        }) satisfies WorkflowParams
    )
    .otherwise(() => undefined)
}

type CreateWorkflowButtonProps = {
  scaleItems: Scale[]
  onCreateWorkflow: (params: WorkflowParams) => unknown
}

export const CreateWorkflowButton: React.FC<CreateWorkflowButtonProps> = ({ scaleItems, onCreateWorkflow }) => {
  const form = useFormContext()
  const values = useStore(form.store, state => state.values)
  const isBranchEndingWithSlash = values.branch.endsWith('/')

  const state = match(values.selectedType)
    .when(
      () => isBranchEndingWithSlash,
      () => 'disabled' as const
    )
    .with('scan', 'pull-request-scan', () => (values.selectedTool ? 'default' : 'disabled'))
    .with('scheduled', () => (values.selectedTool && values.selectedCadence ? 'default' : 'disabled'))
    .otherwise(() => 'disabled' as const)

  return (
    <DefaultButton
      buttonType="primary"
      state={state}
      buttonRole="submit"
      onClick={() => {
        const params = getWorkflowParams(values, scaleItems)
        if (params) {
          onCreateWorkflow(params)
        }
      }}
      data-testid="workflow-drawer-create-workflow-button"
    >
      Create workflow
    </DefaultButton>
  )
}

type ManageWorkflowsFooterButtonProps = {
  onDirtyClick: () => unknown
  onCleanClick: () => unknown
  children: React.ReactNode
}

export const ManageWorkflowsFooterButton: React.FC<ManageWorkflowsFooterButtonProps> = ({
  onDirtyClick,
  onCleanClick,
  children,
}) => {
  const form = useFormContext()
  const selectedType = useStore(form.store, state => state.values.selectedType)
  const selectedTool = useStore(form.store, state => state.values.selectedTool)
  const selectedCadence = useStore(form.store, state => state.values.selectedCadence)

  return (
    <GhostButton
      type="default"
      onClick={e => {
        e.preventDefault()
        const isDirty =
          selectedType === 'scan' || selectedType === 'pull-request-scan'
            ? !!selectedTool
            : !!(selectedTool && selectedCadence)
        if (isDirty) {
          onDirtyClick()
        } else {
          onCleanClick()
        }
      }}
      data-testid="workflow-drawer-manage-workflows-button"
    >
      {children}
    </GhostButton>
  )
}

type AbandonWorkflowDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => unknown
  onAbandon: () => unknown
  repositoryType: Repository['type']
  repositoryDisplayName: string
  displayName: string
}

export const AbandonWorkflowDialog: React.FC<AbandonWorkflowDialogProps> = ({
  isOpen,
  onOpenChange,
  onAbandon,
  repositoryType,
  repositoryDisplayName,
  displayName,
}) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={sharedStyles.dialogOverlay} />
        <Dialog.Content className={sharedStyles.dialogContent}>
          <Dialog.Title className={sharedStyles.dialogTitle}>
            <WarningIcon className={sharedStyles.cautionIcon} size={32} weight="fill" aria-hidden={true} />
            Abandon workflow?
          </Dialog.Title>
          <div className={sharedStyles.dialogMiddle}>
            <Dialog.Description className={sharedStyles.dialogMessage}>
              You have a workflow in progress. How would you like to proceed?
            </Dialog.Description>
            <div className={sharedStyles.dialogSubtitle}>
              <div className={sharedStyles.dialogRepoInfo}>
                <SCMIcon type={repositoryType} size="medium" />
                <span>{repositoryDisplayName}</span>
              </div>
              <div className={sharedStyles.workflowName}>{displayName}</div>
            </div>
          </div>
          <div className={sharedStyles.dialogButtons}>
            <GhostButton type="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="secondary"
              onClick={() => {
                onOpenChange(false)
                onAbandon()
              }}
            >
              Abandon
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export const toCreateWorkflowRequest = (params: WorkflowParams): CreateWorkflowRequest => {
  const event = (
    params.type === 'scheduled'
      ? {
          type: 'schedule' as const,
          cadence: params.cadence,
          start: undefined,
          branch: params.branch || undefined,
        }
      : params.type === 'scan'
        ? {
            type: 'new-scan' as const,
            branch: params.branch || undefined,
          }
        : {
            type: 'pull-request-scan' as const,
            target_branch: params.branch || undefined,
          }
  ) satisfies CreateWorkflowRequest['event']

  if (params.selectedAction === 'none') {
    return {
      tool: params.tool,
      name: params.name || undefined,
      event,
      action: { type: 'none' },
    } satisfies CreateWorkflowRequest
  }

  const isScanType = params.type === 'scan' || params.type === 'pull-request-scan'

  return {
    tool: params.tool,
    name: params.name || undefined,
    event,
    action: {
      type: 'create-patch',
      findings: {
        include_severity_labels: params.selectedSeverities?.length ? params.selectedSeverities : undefined,
        minimum_severity_score: params.minimumSeverityScore,
        maximum_severity_score: params.maximumSeverityScore,
      },
      fixes: params.fixConfidence
        ? {
            minimum_fix_confidence: params.fixConfidence,
            max_count: 1,
          }
        : undefined,
      limit:
        isScanType && params.limitFixes && params.fixesLimit
          ? {
              type: 'finding-count',
              max: params.fixesLimit,
            }
          : undefined,
    },
  } satisfies CreateWorkflowRequest
}
